/**
 * Shared signing model for the newer workflow builders.
 *
 * The steward chooses how each paper gets signed, in the portal or by hand on
 * paper, except where an outside party asks for original ink. That single
 * exception is marked wet_required and carries a plain reason line.
 */

export type SigningMode = 'wet_required' | 'stewards_choice';

export interface SigningSpec {
  /** How this paper may be signed. */
  signing: SigningMode;
  /** For the choice case: true preselects paper, false preselects the portal. */
  defaultInk?: boolean;
  /** Shown on the card. Required for wet_required, optional otherwise. */
  signingReason?: string;
}

export type SigningChoice = 'portal' | 'paper';

export const PAPER_LINE_PLAIN = 'Print it, sign it in blue ink, and scan it to the Record.';

export const PAPER_LINE_WITH_SEAL =
  'Print it, sign it in blue ink, impress your seal if your Association keeps one, and scan it to the Record.';

/** The paper line only mentions a seal when the Association keeps one. */
export function paperLine(sealEnabled?: boolean): string {
  return sealEnabled ? PAPER_LINE_WITH_SEAL : PAPER_LINE_PLAIN;
}

export const BANK_INK_REASON = 'The bank asks for original ink before it updates its records.';

/** Reads the saved choice for a document, falling back to its default. */
export function resolveSigningChoice(mergeFieldData: any): SigningChoice {
  const saved = mergeFieldData?.signing_choice;
  if (saved === 'portal' || saved === 'paper') return saved;
  return mergeFieldData?.default_ink ? 'paper' : 'portal';
}

export function isWetRequired(mergeFieldData: any): boolean {
  return mergeFieldData?.signing === 'wet_required' || mergeFieldData?.wet_ink_required === true;
}
